import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ClipboardList, FileText, MessagesSquare, Home, Stethoscope, ChevronRight,
  Eye, Plus, Trash2, ArrowUp, ArrowDown, Save, UploadCloud, GripVertical } from "lucide-react";
import { http } from "@/lib/api";
import { useSeo } from "@/lib/seo";

const ICONS = { clarity: Stethoscope, blueprint: FileText, guided: MessagesSquare, inhome: Home };

export default function IntakeManager() {
  const nav = useNavigate();
  const [stages, setStages] = useState([]);
  const [open, setOpen] = useState("clarity");
  const [busy, setBusy] = useState("");
  useSeo({ title: "Intake & Workflow Manager", noindex: true });

  const load = () => http.get("/admin/intake-schemas").then((r) => setStages(r.data)).catch(() => toast.error("Could not load intake schemas"));
  useEffect(() => { load(); }, []);

  const patchStage = (key, updater) =>
    setStages((prev) => prev.map((s) => (s.key === key ? updater(s) : s)));

  const editQ = (key, idx, field, value) =>
    patchStage(key, (s) => ({ ...s, draft: s.draft.map((q, i) => (i === idx ? { ...q, [field]: value } : q)), _dirty: true }));

  const moveQ = (key, idx, dir) =>
    patchStage(key, (s) => {
      const j = idx + dir;
      if (j < 0 || j >= s.draft.length) return s;
      const d = [...s.draft]; [d[idx], d[j]] = [d[j], d[idx]];
      return { ...s, draft: d, _dirty: true };
    });

  const addQ = (key) =>
    patchStage(key, (s) => ({ ...s, draft: [...s.draft, { id: `new-${Date.now()}`, label: "", required: true, active: true }], _dirty: true }));

  const delQ = (key, idx) =>
    patchStage(key, (s) => ({ ...s, draft: s.draft.filter((_, i) => i !== idx), _dirty: true }));

  const saveDraft = async (s) => {
    setBusy(`save-${s.key}`);
    try {
      const { data } = await http.put(`/admin/intake-schemas/${s.key}`, { questions: s.draft });
      patchStage(s.key, () => ({ ...data, _dirty: false }));
      toast.success("Draft saved");
    } catch (e) { toast.error("Could not save draft"); } finally { setBusy(""); }
  };

  const publish = async (s) => {
    if (s._dirty) { toast.error("Save the draft before publishing"); return; }
    setBusy(`pub-${s.key}`);
    try {
      const { data } = await http.post(`/admin/intake-schemas/${s.key}/publish`);
      patchStage(s.key, () => ({ ...data, _dirty: false }));
      toast.success(`Published — version ${data.version}`);
    } catch (e) { toast.error("Could not publish"); } finally { setBusy(""); }
  };

  const previewAsCustomer = async (to) => {
    try { await http.post("/owner/impersonate"); window.location.assign(to); }
    catch (e) { toast.error("Could not enter test mode"); }
  };

  return (
    <div className="min-h-screen bg-[#161514] text-[#F7F5F0]">
      <div className="max-w-4xl mx-auto px-5 py-10">
        <button onClick={() => nav("/owner")} className="flex items-center gap-1.5 text-sm text-[#A39E98] hover:text-[#F7F5F0] mb-6"><ArrowLeft size={15} /> Owner Control Center</button>
        <div className="eyebrow text-[#C2836B] mb-1 flex items-center gap-2"><ClipboardList size={14} /> Intake & Workflow Manager</div>
        <h1 className="font-display text-3xl sm:text-4xl mb-2">Edit every intake, then publish</h1>
        <p className="text-[#A39E98] text-sm mb-8 max-w-2xl">Add, edit, reorder, and toggle required / active for each stage's intake questions. Save a draft, then publish to make it the question set of record. Preview any intake as a test customer, or open the live consultant builder to draft & publish the deliverable.</p>

        <div className="space-y-3">
          {stages.map((s) => {
            const Icon = ICONS[s.key] || ClipboardList;
            const unpublished = s._dirty || s.has_unpublished_changes;
            return (
              <div key={s.key} className="card-night rounded-2xl overflow-hidden" data-testid={`stage-${s.key}`}>
                <button onClick={() => setOpen(open === s.key ? "" : s.key)} className="w-full flex items-center justify-between gap-3 p-5 text-left">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#C2836B]/15 grid place-items-center"><Icon size={18} className="text-[#C2836B]" /></div>
                    <div>
                      <h2 className="font-display text-lg flex items-center gap-2">{s.title}
                        {unpublished && <span className="text-[10px] font-sans tracking-wide uppercase bg-[#C49A5A]/20 text-[#d7ac63] rounded-full px-2 py-0.5">Unpublished changes</span>}
                      </h2>
                      <p className="text-[#A39E98] text-xs">{s.intake_label} · v{s.version} · {s.draft?.length || 0} questions</p>
                    </div>
                  </div>
                  <ChevronRight size={18} className={`text-[#738075] transition-transform ${open === s.key ? "rotate-90" : ""}`} />
                </button>

                {open === s.key && (
                  <div className="px-5 pb-5 border-t border-[#2E2C2A] pt-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="eyebrow text-[#738075]">Intake questions (draft)</div>
                      <button data-testid={`add-question-${s.key}`} onClick={() => addQ(s.key)}
                        className="text-xs flex items-center gap-1.5 bg-[#C2836B]/15 border border-[#C2836B]/40 rounded-full px-3 py-1.5 text-[#C2836B] hover:bg-[#C2836B]/25 transition-colors"><Plus size={12} /> Add question</button>
                    </div>

                    <div className="space-y-2" data-testid={`questions-${s.key}`}>
                      {s.draft?.map((q, idx) => (
                        <div key={q.id} className={`rounded-xl border p-3 flex items-start gap-2 ${q.active ? "border-[#2E2C2A] bg-[#1C1B1A]" : "border-[#2E2C2A]/60 bg-[#1C1B1A]/50 opacity-70"}`}>
                          <GripVertical size={16} className="text-[#5a5751] mt-2 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <input data-testid={`q-label-${s.key}-${idx}`} value={q.label} placeholder="Question label"
                              onChange={(e) => editQ(s.key, idx, "label", e.target.value)}
                              className="w-full bg-transparent border-b border-[#2E2C2A] focus:border-[#C2836B] outline-none text-[#F7F5F0] text-sm py-1.5" />
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                              <Chip active={q.required} onClick={() => editQ(s.key, idx, "required", !q.required)} tid={`q-required-${s.key}-${idx}`}
                                on="Required" off="Optional" />
                              <Chip active={q.active} onClick={() => editQ(s.key, idx, "active", !q.active)} tid={`q-active-${s.key}-${idx}`}
                                on="Active" off="Inactive" tone="sage" />
                            </div>
                          </div>
                          <div className="flex flex-col gap-1 shrink-0">
                            <div className="flex gap-1">
                              <IconBtn onClick={() => moveQ(s.key, idx, -1)} disabled={idx === 0} tid={`q-up-${s.key}-${idx}`}><ArrowUp size={13} /></IconBtn>
                              <IconBtn onClick={() => moveQ(s.key, idx, 1)} disabled={idx === s.draft.length - 1} tid={`q-down-${s.key}-${idx}`}><ArrowDown size={13} /></IconBtn>
                            </div>
                            <IconBtn onClick={() => delQ(s.key, idx)} danger tid={`q-del-${s.key}-${idx}`}><Trash2 size={13} /></IconBtn>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="flex flex-wrap items-center gap-2 mt-4">
                      <button data-testid={`save-draft-${s.key}`} onClick={() => saveDraft(s)} disabled={busy === `save-${s.key}`}
                        className="text-sm flex items-center gap-1.5 bg-[#C2836B] text-[#161514] rounded-full px-4 py-2 font-medium hover:bg-[#d2927a] transition-colors disabled:opacity-50">
                        <Save size={14} /> {busy === `save-${s.key}` ? "Saving…" : "Save draft"}</button>
                      <button data-testid={`publish-${s.key}`} onClick={() => publish(s)} disabled={busy === `pub-${s.key}` || s._dirty}
                        className="text-sm flex items-center gap-1.5 bg-[#667A66]/20 border border-[#667A66]/50 text-[#a9d3a9] rounded-full px-4 py-2 hover:bg-[#667A66]/30 transition-colors disabled:opacity-40">
                        <UploadCloud size={14} /> {busy === `pub-${s.key}` ? "Publishing…" : "Publish changes"}</button>
                      <button data-testid={`preview-intake-${s.key}`} onClick={() => previewAsCustomer(s.preview)}
                        className="text-sm flex items-center gap-1.5 bg-[#1C1B1A] border border-[#2E2C2A] rounded-full px-4 py-2 text-[#A39E98] hover:text-[#F7F5F0] transition-colors"><Eye size={13} /> Preview as customer</button>
                      <button data-testid={`open-builder-${s.key}`} onClick={() => nav("/operator")}
                        className="text-sm flex items-center gap-1.5 bg-[#1C1B1A] border border-[#2E2C2A] rounded-full px-4 py-2 text-[#A39E98] hover:text-[#F7F5F0] transition-colors"><FileText size={13} /> Open live builder</button>
                    </div>

                    <div className="mt-5 border-t border-[#2E2C2A] pt-4">
                      <div className="eyebrow text-[#738075] mb-2">Deliverable — {s.deliverable}</div>
                      <div className="flex flex-wrap gap-1.5">
                        {s.sections?.map((sec) => <span key={sec} className="text-xs text-[#D9D4CC] bg-[#1C1B1A] border border-[#2E2C2A] rounded-full px-2.5 py-1">{sec}</span>)}
                      </div>
                      <p className="text-[11px] text-[#6b665e] mt-3 leading-relaxed">Deliverable sections are authored per customer in the consultant workspace (draft → preview → publish).</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Chip({ active, onClick, on, off, tone = "clay", tid }) {
  const onCls = tone === "sage" ? "bg-[#667A66]/20 border-[#667A66]/50 text-[#a9d3a9]" : "bg-[#C2836B]/15 border-[#C2836B]/40 text-[#C2836B]";
  return (
    <button data-testid={tid} onClick={onClick}
      className={`text-[11px] rounded-full border px-2.5 py-1 transition-colors ${active ? onCls : "bg-[#1C1B1A] border-[#2E2C2A] text-[#6b665e]"}`}>
      {active ? on : off}
    </button>
  );
}
function IconBtn({ onClick, disabled, danger, children, tid }) {
  return (
    <button data-testid={tid} onClick={onClick} disabled={disabled}
      className={`w-7 h-7 grid place-items-center rounded-lg border border-[#2E2C2A] transition-colors disabled:opacity-30 ${danger ? "text-[#c77] hover:border-[#c77]/50" : "text-[#A39E98] hover:text-[#F7F5F0] hover:border-[#C2836B]/50"}`}>
      {children}
    </button>
  );
}
